/**
 * FilePickerField - File/folder selection with tree navigation
 *
 * Supports x-ms-dynamic-tree pattern from Azure connectors:
 * - SharePoint file/folder pickers
 * - OneDrive file browsers
 * - Azure Blob container/blob pickers
 *
 * Two picker variants, selected automatically from `dynamicTree.settings`:
 *  - `folder` picker (canSelectParentNodes && !canSelectLeafNodes): only
 *    folders are shown. Clicking a folder commits the selection. Use the
 *    `>` chevron button to navigate into it.
 *  - `file` picker (default): files and folders both shown. Clicking a file
 *    commits the selection. Clicking a folder navigates into it. The `>`
 *    chevron is omitted because the row click already navigates.
 *
 * Mirrors LogicAppsUX `FilePickerEditor` semantics (PickerItemType.FOLDER /
 * .FILE, MenuSplitGroup body+chevron). No bottom Select/Cancel buttons:
 * selection commits immediately on click and closes the popover.
 *
 * This component replaces the legacy FilePickerEditor and works with
 * raw strings instead of ValueSegment[].
 */

import {
  makeStyles,
  mergeClasses,
  Button,
  Label,
  Text,
  Spinner,
  tokens,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Input,
} from '@fluentui/react-components';
import {
  Folder20Regular,
  Document20Regular,
  ChevronRight20Regular,
  ArrowClockwise16Regular,
  FolderOpen20Regular,
} from '@fluentui/react-icons';
import { useMemo, useCallback, useState, type ReactNode } from 'react';
import type { DynamicTreeConfig } from '@/types/operationParameter';
import { useDynamicTree, type TreeItem } from '@/hooks/useDynamicTree';
import { useNodeDynamicDataContext } from '@/hooks/useNodeDynamicDataContext';
import { customScrollbarStyles } from '@/styles/scrollbar';
import { EndActionsSlot } from '../shared/EndActionsSlot';
import { derivePickerType, filterVisibleItems, isItemInteractive } from './pickerSettings';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    width: '100%',
  },
  // Top row that hosts the field label and (optionally) the
  // `FieldActionChips` end-actions slot. Mirrors the labelRow used by
  // StringField / NumberField so chips anchor above the input + Browse
  // row instead of overlapping the Browse button via an absolute
  // top-right `externalChipSlot`.
  labelRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalS,
    minHeight: '20px',
  },
  label: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
  },
  // Vertical stack holding the label row (label + end-actions chips) and
  // the input itself. Sized via `flex: 1` so chips align to the input's
  // right edge rather than spanning across the sibling Browse button.
  inputStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    flexGrow: 1,
    minWidth: 0,
  },
  inputRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: tokens.spacingHorizontalXS,
    width: '100%',
  },
  input: {
    flexGrow: 1,
  },
  browseButton: {
    minWidth: 'auto',
  },
  popover: {
    width: '400px',
    maxHeight: '400px',
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
  },
  breadcrumbBar: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground3,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    flexWrap: 'wrap',
    minHeight: '36px',
  },
  breadcrumbItem: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  breadcrumbCurrent: {
    fontWeight: tokens.fontWeightSemibold,
    cursor: 'default',
    '&:hover': {
      textDecoration: 'none',
    },
  },
  breadcrumbSeparator: {
    color: tokens.colorNeutralForeground3,
    marginLeft: tokens.spacingHorizontalXXS,
    marginRight: tokens.spacingHorizontalXXS,
  },
  // The list scrolls and uses our shared subtle scrollbar styles. `position:
  // relative` anchors the loading overlay shown during folder navigation.
  itemsList: {
    flexGrow: 1,
    minHeight: '160px',
    overflowY: 'auto',
    padding: tokens.spacingVerticalXS,
    position: 'relative',
    ...customScrollbarStyles,
  },
  treeItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    cursor: 'pointer',
    borderRadius: tokens.borderRadiusMedium,
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  treeItemDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  treeItemIcon: {
    flexShrink: 0,
    color: tokens.colorNeutralForeground2,
  },
  folderIcon: {
    color: tokens.colorPaletteYellowForeground1,
  },
  treeItemText: {
    flexGrow: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // Chevron-as-button used in folder pickers to navigate into a folder
  // without selecting it. Click stops propagation so the row click (which
  // would commit the selection) doesn't also fire.
  // A subtle left border separates the navigation hit-target from the row
  // body so users can tell at a glance the chevron is its own action.
  navChevronButton: {
    flexShrink: 0,
    minWidth: 'auto',
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalXS}`,
    marginLeft: tokens.spacingHorizontalXS,
    paddingLeft: tokens.spacingHorizontalS,
    borderLeft: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: 0,
  },
  // Static chevron shown next to folders in a *file* picker as an affordance
  // that the row navigates. Not interactive on its own; the row click drives
  // navigation.
  navChevronStatic: {
    flexShrink: 0,
    color: tokens.colorNeutralForeground3,
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalL,
    color: tokens.colorNeutralForeground3,
  },
  // Translucent overlay used during folder navigation so the previous folder's
  // items remain visible (slightly faded) while the new folder loads, instead
  // of clearing the list and producing a flicker.
  loadingOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalS,
    backgroundColor: tokens.colorNeutralBackground1,
    opacity: 0.7,
    pointerEvents: 'none',
    zIndex: 1,
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalL,
    textAlign: 'center',
  },
  errorText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
  },
  emptyContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalL,
    color: tokens.colorNeutralForeground3,
  },
  waitingText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
  refreshButtonAuto: {
    marginLeft: 'auto',
  },
});

export interface FilePickerFieldProps {
  /** Stable identifier (e.g. parameter name). */
  name: string;
  /** Accessible label for the input. */
  label?: string | undefined;
  /** Current selected value (the workflow value — e.g. an encoded item id). */
  value: string;
  /**
   * Friendly display for the current value (typically a human-readable
   * path). Pass when the parent persists a value→display mapping (e.g.
   * the editor's picker metadata) so the input shows the path instead of
   * the raw id after a workflow reload.
   */
  displayValue?: string | undefined;
  /**
   * Emits a change. First arg is the workflow value (swagger `value-path`,
   * usually an encoded id). Second arg is the display (swagger
   * `itemFullTitlePath`); pass `undefined` to clear any persisted display.
   */
  onChange: (value: string, displayValue?: string) => void;
  /** x-ms-dynamic-tree configuration from the parameter manifest. */
  dynamicTree?: DynamicTreeConfig | undefined;
  placeholder?: string | undefined;
  disabled?: boolean | undefined;
  readOnly?: boolean | undefined;
  className?: string | undefined;
  'data-testid'?: string | undefined;
  /**
   * Optional slot rendered in the field's labelRow on the right edge.
   * Used to host inline affordances like `FieldActionChips` so they sit
   * above the input + Browse row rather than overlapping the Browse
   * button.
   */
  endActions?: ReactNode | undefined;
}

/**
 * FilePickerField component
 *
 * Renders a file/folder picker with tree navigation using x-ms-dynamic-tree.
 */
export const FilePickerField = ({
  name,
  label,
  value,
  displayValue,
  onChange,
  dynamicTree,
  placeholder,
  disabled = false,
  readOnly = false,
  className,
  'data-testid': testId,
  endActions,
}: FilePickerFieldProps) => {
  const styles = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  // Show the parent-supplied display when present (round-tripped via
  // workflow `metadata`), otherwise fall back to the raw value.
  const inputDisplay = displayValue ?? value;

  const { connectionReference, connectorId, parameterValues } = useNodeDynamicDataContext();

  // Use dynamic tree hook
  const {
    items,
    breadcrumb,
    isLoading,
    error,
    dependenciesSatisfied,
    missingDependencies,
    missingConnection,
    openFolder,
    navigateTo,
    clearSelection,
    refresh,
    goToRoot,
  } = useDynamicTree({
    treeConfig: dynamicTree,
    connectionReference,
    connectorId,
    parameterValues,
    autoFetch: isOpen,
  });

  // Determine picker variant from x-ms-dynamic-tree settings: folder picker
  // (canSelectParentNodes && !canSelectLeafNodes) shows only folders and
  // commits on row click, with a `>` chevron for navigation. File picker
  // (the default) shows files + folders; row click selects files and
  // navigates folders.
  const pickerType = useMemo(() => derivePickerType(dynamicTree?.settings), [dynamicTree]);

  const visibleItems = useMemo(
    () => filterVisibleItems(items, pickerType),
    [items, pickerType]
  );

  // Commit a selection: writes the swagger `value-path` (item id) as the
  // workflow value and the `itemFullTitlePath` (friendly path) as the
  // display. Mirrors LogicAppsUX `getValueFromPickerSelectedItem` /
  // `getDisplayValueFromPickerSelectedItem`.
  const commitSelection = useCallback(
    (item: TreeItem) => {
      onChange(item.value, item.fullPath ?? item.displayName);
      setIsOpen(false);
    },
    [onChange]
  );

  // Row body click: in a folder picker, selects the folder; in a file
  // picker, a folder navigates and a file selects. Non-interactive rows
  // (e.g. unselectable files in folder-picker mode that slipped past the
  // filter) do nothing.
  const handleItemClick = useCallback(
    (item: TreeItem) => {
      if (pickerType === 'folder') {
        if (item.isFolder) commitSelection(item);
        return;
      }
      if (item.isFolder) {
        openFolder(item);
        return;
      }
      if (item.isSelectable) {
        commitSelection(item);
      }
    },
    [pickerType, commitSelection, openFolder]
  );

  // Manual input edits clear the picker display (the parent sees `undefined`
  // and removes the metadata mapping for the previously-picked id).
  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(event.target.value);
    },
    [onChange]
  );

  const hasLabelRow = Boolean(label) || Boolean(endActions);
  const labelRow = hasLabelRow ? (
    <div className={styles.labelRow}>
      {label ? (
        <Label className={styles.label} htmlFor={name}>
          {label}
        </Label>
      ) : (
        <span />
      )}
      {endActions ? <EndActionsSlot>{endActions}</EndActionsSlot> : null}
    </div>
  ) : null;

  // Show waiting state when the connection reference is missing or a
  // placeholder shell. Takes precedence over dependency waiting — without
  // a connection, dependency resolution is meaningless.
  if (missingConnection) {
    return (
      <div className={styles.container}>
        <div className={styles.inputRow}>
          <div className={styles.inputStack}>
            {labelRow}
            <Input
              id={name}
              className={mergeClasses(styles.input, className)}
              value={inputDisplay}
              onChange={handleInputChange}
              disabled
              placeholder="Connection required..."
              aria-label={label ?? name}
              data-testid={testId}
            />
          </div>
        </div>
        <Text className={styles.waitingText}>
          Configure a connection to browse items.
        </Text>
      </div>
    );
  }

  // Show waiting for dependencies message
  if (!dependenciesSatisfied && missingDependencies.length > 0) {
    return (
      <div className={styles.container}>
        <div className={styles.inputRow}>
          <div className={styles.inputStack}>
            {labelRow}
            <Input
              id={name}
              className={mergeClasses(styles.input, className)}
              value={inputDisplay}
              onChange={handleInputChange}
              disabled
              placeholder="Select required fields first..."
              aria-label={label ?? name}
              data-testid={testId}
            />
          </div>
        </div>
        <Text className={styles.waitingText}>
          Select: {missingDependencies.join(', ')}
        </Text>
      </div>
    );
  }

  const browseLabel = pickerType === 'folder' ? 'Browse for folder' : 'Browse for file';

  return (
    <div className={styles.container}>
      <div className={styles.inputRow}>
        <div className={styles.inputStack}>
          {labelRow}
          <Input
            id={name}
            className={mergeClasses(styles.input, className)}
            value={inputDisplay}
            onChange={handleInputChange}
            disabled={disabled || readOnly}
            placeholder={
              placeholder ?? (pickerType === 'folder' ? 'Select a folder...' : 'Select a file or folder...')
            }
            aria-label={label ?? name}
            data-testid={testId}
          />
        </div>
        <Popover
          open={isOpen}
          onOpenChange={(_, data) => {
            setIsOpen(data.open);
            if (!data.open) {
              // Reset transient selection state and rewind to root so the next
              // open starts fresh. `goToRoot` cancels any in-flight browse.
              clearSelection();
              goToRoot();
            }
          }}
          positioning="below-end"
        >
          <PopoverTrigger disableButtonEnhancement>
            <Button
              className={styles.browseButton}
              icon={<FolderOpen20Regular />}
              disabled={disabled || readOnly || !dynamicTree}
              aria-label={browseLabel}
            >
              Browse
            </Button>
          </PopoverTrigger>
          <PopoverSurface className={styles.popover}>
            {/* Breadcrumb navigation */}
            <div className={styles.breadcrumbBar}>
              {breadcrumb.map((crumb, index) => {
                const isLast = index === breadcrumb.length - 1;
                return (
                  <span key={index} className={styles.breadcrumbItem}>
                    {index > 0 && (
                      <ChevronRight20Regular className={styles.breadcrumbSeparator} />
                    )}
                    <Text
                      className={isLast ? styles.breadcrumbCurrent : undefined}
                      onClick={isLast ? undefined : () => navigateTo(index)}
                    >
                      {crumb.label}
                    </Text>
                  </span>
                );
              })}
              <Button
                appearance="subtle"
                icon={<ArrowClockwise16Regular />}
                onClick={refresh}
                title="Refresh"
                aria-label="Refresh"
                className={styles.refreshButtonAuto}
              />
            </div>

            {/* Items list */}
            <div className={styles.itemsList}>
              {/* Initial load: show full-area spinner only when there are no
                  items yet (first open of the picker). */}
              {isLoading && visibleItems.length === 0 && !error && (
                <div className={styles.loadingContainer}>
                  <Spinner size="small" />
                  <Text>Loading...</Text>
                </div>
              )}

              {/* Folder navigation: keep the previous folder's items visible
                  but dim them with an overlay so the user gets immediate
                  feedback that a request is in flight. */}
              {isLoading && visibleItems.length > 0 && (
                <div className={styles.loadingOverlay} aria-live="polite" aria-busy="true">
                  <Spinner size="tiny" />
                  <Text>Loading...</Text>
                </div>
              )}

              {error && (
                <div className={styles.errorContainer}>
                  <Text className={styles.errorText}>{error}</Text>
                  <Button appearance="secondary" icon={<ArrowClockwise16Regular />} onClick={refresh}>
                    Retry
                  </Button>
                </div>
              )}

              {!isLoading && !error && visibleItems.length === 0 && (
                <div className={styles.emptyContainer}>
                  <Text>{pickerType === 'folder' ? 'No folders found' : 'No items found'}</Text>
                </div>
              )}

              {visibleItems.map((item) => {
                const isFolder = item.isFolder;
                const isInteractive = isItemInteractive(item, pickerType);
                const showNavChevronButton = isFolder && pickerType === 'folder';
                const showStaticNavChevron = isFolder && pickerType === 'file';

                return (
                  <div
                    key={item.value}
                    className={mergeClasses(
                      styles.treeItem,
                      !isInteractive && styles.treeItemDisabled
                    )}
                    onClick={() => isInteractive && handleItemClick(item)}
                    role="button"
                    tabIndex={isInteractive ? 0 : -1}
                    onKeyDown={(e) => {
                      if (!isInteractive) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleItemClick(item);
                      }
                    }}
                    aria-disabled={!isInteractive}
                  >
                    {isFolder ? (
                      <Folder20Regular className={mergeClasses(styles.treeItemIcon, styles.folderIcon)} />
                    ) : (
                      <Document20Regular className={styles.treeItemIcon} />
                    )}
                    <Text className={styles.treeItemText} title={item.displayName}>
                      {item.displayName}
                    </Text>
                    {showNavChevronButton && (
                      <Button
                        appearance="subtle"
                        size="small"
                        className={styles.navChevronButton}
                        icon={<ChevronRight20Regular />}
                        title={`Open ${item.displayName}`}
                        aria-label={`Open ${item.displayName}`}
                        onClick={(e) => {
                          // Stop propagation so the row's click handler
                          // (which would commit selection in folder-picker
                          // mode) doesn't also fire.
                          e.stopPropagation();
                          openFolder(item);
                        }}
                      />
                    )}
                    {showStaticNavChevron && (
                      <ChevronRight20Regular className={styles.navChevronStatic} />
                    )}
                  </div>
                );
              })}
            </div>
          </PopoverSurface>
        </Popover>
      </div>
    </div>
  );
};

FilePickerField.displayName = 'FilePickerField';
